/**
 * Minimal SSE (Server-Sent Events) parser for LLM Chat Completions streaming (#89098).
 *
 * Self-implemented to avoid pulling in the `openai` runtime package or any
 * Node-specific API. Uses only Web-standard `ReadableStream` + `TextDecoder`.
 *
 * Handles:
 * - Standard SSE `data:` / `event:` fields
 * - Multi-line `data:` fields (joined with \n per SSE spec)
 * - UTF-8 cross-chunk boundary (via TextDecoder with stream=true)
 * - `[DONE]` sentinel
 * - `event: error` dispatch
 * - CRLF and LF line endings
 * - AbortSignal — cancels the stream reader cleanly
 */

export interface SSEEvent {
    /** The event type (null for default/unspecified events). */
    event: string | null;
    /** The data payload (multiple `data:` lines joined with \n). */
    data: string;
}

/**
 * SSEDecoder processes complete SSE lines and assembles them into events.
 *
 * An SSE event is terminated by an empty line (double newline).
 * Lines starting with `:` are comments and are ignored.
 */
class SSEDecoder {
    private event: string | null = null;
    private dataParts: string[] = [];

    /**
     * Feed a single line. Returns an SSEEvent when the current event is
     * terminated (i.e. when an empty line is received), or null otherwise.
     */
    decode(line: string): SSEEvent | null {
        // Strip trailing \r for CRLF compatibility
        let l = line;
        if (l.endsWith('\r')) {
            l = l.slice(0, -1);
        }

        // Empty line = event terminator
        if (l === '') {
            if (this.event === null && this.dataParts.length === 0) {
                return null;
            }
            const ev: SSEEvent = {
                event: this.event,
                data: this.dataParts.join('\n'),
            };
            this.event = null;
            this.dataParts = [];
            return ev;
        }

        // Comment line (starts with ':')
        if (l.startsWith(':')) {
            return null;
        }

        // Parse field: value
        const colonIdx = l.indexOf(':');
        let field: string;
        let value: string;
        if (colonIdx === -1) {
            field = l;
            value = '';
        } else {
            field = l.slice(0, colonIdx);
            // Per SSE spec, a single leading space after the colon is removed
            let rest = l.slice(colonIdx + 1);
            if (rest.startsWith(' ')) {
                rest = rest.slice(1);
            }
            value = rest;
        }

        if (field === 'event') {
            this.event = value;
        } else if (field === 'data') {
            this.dataParts.push(value);
        }
        // Ignore id/retry fields — not needed for this SDK

        return null;
    }

    /** Flush any remaining buffered data as a final event. */
    flush(): SSEEvent | null {
        if (this.event === null && this.dataParts.length === 0) {
            return null;
        }
        const ev: SSEEvent = {
            event: this.event,
            data: this.dataParts.join('\n'),
        };
        this.event = null;
        this.dataParts = [];
        return ev;
    }
}

/**
 * LineDecoder splits a byte stream into individual lines (by \n or \r\n).
 *
 * Operates on Uint8Array to correctly handle UTF-8 multi-byte characters
 * that may be split across chunks. Uses TextDecoder with stream=true
 * for incremental decoding.
 */
class LineDecoder {
    private buffer: Uint8Array = new Uint8Array();
    private decoder: TextDecoder;

    constructor() {
        // stream=true keeps incomplete multi-byte sequences buffered internally
        this.decoder = new TextDecoder('utf-8');
    }

    /**
     * Decode a chunk and yield complete lines.
     *
     * Lines are terminated by \n, \r, or \r\n. The line terminators are
     * NOT included in the returned strings.
     */
    decode(chunk: Uint8Array): string[] {
        // Append to buffer
        const newData = new Uint8Array(this.buffer.length + chunk.length);
        newData.set(this.buffer);
        newData.set(chunk, this.buffer.length);
        this.buffer = newData;

        const lines: string[] = [];
        let start = 0;

        for (let i = 0; i < this.buffer.length; i++) {
            if (this.buffer[i] === 0x0a /* \n */ || this.buffer[i] === 0x0d /* \r */) {
                // Decode the line (excluding the terminator)
                const lineBytes = this.buffer.subarray(start, i);
                const line = this.decoder.decode(lineBytes, { stream: true });
                lines.push(line);

                // Handle \r\n: skip the \n
                if (this.buffer[i] === 0x0d && i + 1 < this.buffer.length && this.buffer[i + 1] === 0x0a) {
                    i++;
                }
                start = i + 1;
            }
        }

        // Keep the remainder in buffer
        if (start > 0) {
            this.buffer = this.buffer.slice(start);
        }

        return lines;
    }

    /**
     * Flush remaining buffered data. Returns a final line if any data remains
     * (without a trailing newline). The TextDecoder is also flushed.
     */
    flush(): string[] {
        if (this.buffer.length === 0) {
            // Flush any remaining bytes in the TextDecoder
            const remaining = this.decoder.decode();
            return remaining ? [remaining] : [];
        }
        const line = this.decoder.decode(this.buffer, { stream: false });
        this.buffer = new Uint8Array();
        return line ? [line] : [];
    }
}

/** Sentinel value indicating end of stream. */
export const SSE_DONE = '[DONE]';

/**
 * Async generator that yields SSE events from a ReadableStream<Uint8Array>.
 *
 * This is the core streaming primitive: it takes a raw response body
 * and yields parsed SSEEvent objects one at a time.
 *
 * When an AbortSignal is provided and fires, the stream is cancelled
 * (which causes the pending reader.read() to reject), allowing the
 * generator to exit cleanly.
 */
export async function* iterSSEEvents(
    body: ReadableStream<Uint8Array>,
    signal?: AbortSignal,
): AsyncGenerator<SSEEvent, void, unknown> {
    const sseDecoder = new SSEDecoder();
    const lineDecoder = new LineDecoder();
    const reader = body.getReader();

    // When the abort signal fires, cancel the stream to unblock reader.read()
    let aborted = false;
    const onAbort = () => {
        aborted = true;
        reader.cancel().catch(() => {});
    };

    if (signal) {
        if (signal.aborted) {
            return;
        }
        signal.addEventListener('abort', onAbort, { once: true });
    }

    try {
        while (true) {
            if (aborted) {
                return;
            }

            let result: ReadableStreamReadResult<Uint8Array>;
            try {
                result = await reader.read();
            } catch (err) {
                // reader.read() rejects when the stream is cancelled (abort)
                if (aborted || (err instanceof Error && err.name === 'AbortError')) {
                    return;
                }
                throw err;
            }

            const { done, value } = result;
            if (done) break;

            for (const line of lineDecoder.decode(value)) {
                const ev = sseDecoder.decode(line);
                if (ev) yield ev;
            }
        }

        // Flush remaining data
        for (const line of lineDecoder.flush()) {
            const ev = sseDecoder.decode(line);
            if (ev) yield ev;
        }
        const finalEv = sseDecoder.flush();
        if (finalEv) yield finalEv;
    } finally {
        if (signal) {
            signal.removeEventListener('abort', onAbort);
        }
        try {
            reader.releaseLock();
        } catch {
            // already released or stream errored
        }
    }
}
